import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { isComponentType } from '@backstage/plugin-catalog';
import { isAzureWebSiteNameAvailable } from '@backstage-community/plugin-azure-sites';
import { Direction } from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

/**
 * The entity page is assembled from extensions now, not from a JSX tree in the
 * app. Almost everything the old `EntityPage.tsx` rendered by hand is provided
 * by the plugins themselves — the about, links, relations, ownership, profile
 * and API cards, and the Overview, TechDocs, APIs, Definition and Kubernetes
 * tabs — including the orphan/relation/processing-error warnings, which the
 * default overview layout renders above the cards.
 *
 * What is left here is the portal's own content, plus the one plugin that has
 * no new frontend system support.
 */

/**
 * Reads the `clearistic.tech/subject-matter-experts` annotation. The card
 * renders nothing without it; the kind filter keeps it off the entity kinds
 * that never carry it, matching where the old entity page placed it.
 */
export const subjectMatterExpertsCard = EntityCardBlueprint.make({
  name: 'subject-matter-experts',
  params: {
    type: 'info',
    filter: { kind: { $in: ['component', 'system'] } },
    loader: () =>
      import('./components/SubjectMatterExpertCard').then(m => (
        <m.SubjectMatterExpertsCard />
      )),
  },
});

/**
 * `@backstage-community/plugin-azure-sites` is still old frontend system only,
 * so its entity tab is declared here rather than discovered. Its API is
 * registered from `apis.ts` for the same reason.
 */
export const azureSitesEntityContent = EntityContentBlueprint.make({
  name: 'azure',
  params: {
    path: '/azure',
    title: 'Azure',
    group: 'deployment',
    filter: entity => Boolean(isAzureWebSiteNameAvailable(entity)),
    loader: () =>
      import('@backstage-community/plugin-azure-sites').then(m => (
        <m.EntityAzureSitesOverviewWidget />
      )),
  },
});

/**
 * A whole-system relation graph, wider than the relations card on the overview
 * tab. Systems only, as before.
 */
export const systemDiagramEntityContent = EntityContentBlueprint.make({
  name: 'diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    group: 'development',
    filter: { kind: 'system' },
    loader: () =>
      import('@backstage/plugin-catalog-graph').then(m => (
        <m.EntityCatalogGraphCard
          direction={Direction.TOP_BOTTOM}
          title="System Diagram"
          height={700}
          relations={[
            RELATION_PART_OF,
            RELATION_HAS_PART,
            RELATION_API_CONSUMED_BY,
            RELATION_API_PROVIDED_BY,
            RELATION_CONSUMES_API,
            RELATION_PROVIDES_API,
            RELATION_DEPENDENCY_OF,
            RELATION_DEPENDS_ON,
          ]}
          unidirectional={false}
        />
      )),
  },
});

/**
 * A placeholder tab, carried over from the old entity page. No CI/CD plugin is
 * installed; this tells people where one would appear.
 */
export const cicdEntityContent = EntityContentBlueprint.make({
  name: 'ci-cd',
  params: {
    path: '/ci-cd',
    title: 'CI/CD',
    group: 'development',
    filter: entity =>
      isComponentType('service')(entity) || isComponentType('website')(entity),
    loader: () =>
      import('./components/CicdPlaceholder').then(m => <m.CicdPlaceholder />),
  },
});

export const entityExtensions = [
  subjectMatterExpertsCard,
  azureSitesEntityContent,
  systemDiagramEntityContent,
  cicdEntityContent,
];
